import { ethers } from 'ethers';
import logger from './logger';

export interface AssetBalance {
    type: 'ETH' | 'WETH' | 'TOKEN' | 'LP' | 'NFT';
    symbol: string;
    balance: string;
    decimals?: number;
    tokenId?: string; // For NFTs
}

export interface ContractAssetConfig {
    contractType: 'multisig' | 'liquidityPool' | 'positionManager' | 'regular';
    assetTypes: ('ETH' | 'WETH' | 'TOKEN' | 'LP' | 'NFT')[];
    tokenAddresses?: {
        WETH?: string;
        MEMER?: string;
        [key: string]: string | undefined;
    };
}

export class MultiAssetBalanceFetcher {
    private provider: ethers.providers.Web3Provider;
    private deploymentDetails: any;

    constructor(provider: ethers.providers.Web3Provider, deploymentDetails: any) {
        this.provider = provider;
        this.deploymentDetails = deploymentDetails;
    }

    /**
     * Dynamically fetch WETH address from UniswapLiquiditySetup contract
     */
    private async getWETHAddress(): Promise<string | null> {
        try {
            const uniswapSetup = this.deploymentDetails?.liquidity?.uniswapSetup;
            if (!uniswapSetup?.addressForUI) {
                return null;
            }

            const contract = new ethers.Contract(
                uniswapSetup.addressForUI,
                ['function i_wethAddress() view returns (address)'],
                this.provider
            );

            const wethAddress = await contract.i_wethAddress();
            logger.log(`[MultiAssetFetcher] Retrieved WETH address from UniswapLiquiditySetup: ${wethAddress}`);
            return wethAddress;
        } catch (error) {
            logger.warn(`[MultiAssetFetcher] Could not fetch WETH address from UniswapLiquiditySetup:`, error);
            return null;
        }
    }

    /**
     * Get asset configuration for a contract based on deployment details
     */
    private getContractAssetConfig(contractName: string, contractAddress: string): ContractAssetConfig {
        // Default configuration
        const defaultConfig: ContractAssetConfig = {
            contractType: 'regular',
            assetTypes: ['ETH', 'TOKEN'],
            tokenAddresses: {}
        };

        // Extract token addresses from deployment details
        const tokenAddresses: { [key: string]: string } = {};

        // Get MEMER token address
        if (this.deploymentDetails?.core?.memerCore?.addressForUI) {
            tokenAddresses.MEMER = this.deploymentDetails.core.memerCore.addressForUI;
        }

        // Get WETH address from deployment details if available
        if (this.deploymentDetails?.tokens?.WETH?.addressForUI) {
            tokenAddresses.WETH = this.deploymentDetails.tokens.WETH.addressForUI;
        } else {
            // Try to find WETH address from liquidity setup contracts
            // UniswapLiquiditySetup should have i_wethAddress
            const uniswapSetup = this.deploymentDetails?.liquidity?.uniswapSetup;
            if (uniswapSetup?.addressForUI) {
                // We'll try to get WETH address by calling the contract
                logger.log('[MultiAssetFetcher] Will attempt to get WETH address from UniswapLiquiditySetup');
            }
        }

        // Determine contract type and asset types based on contract name and deployment structure
        const lowerName = contractName.toLowerCase();

        if (lowerName.includes('multisig') || lowerName.includes('treasury') ||
            lowerName.includes('founder') || lowerName.includes('charity') ||
            lowerName.includes('marketing')) {
            return {
                contractType: 'multisig',
                assetTypes: ['ETH', 'TOKEN'],
                tokenAddresses
            };
        }

        if (lowerName.includes('pool')) {
            return {
                contractType: 'liquidityPool',
                assetTypes: ['ETH', 'WETH', 'TOKEN'],
                tokenAddresses
            };
        }

        if (lowerName.includes('uniswapsetup')) {
            return {
                contractType: 'positionManager',
                assetTypes: ['ETH', 'TOKEN', 'NFT'],
                tokenAddresses
            };
        }

        if (lowerName.includes('aerodromesetup') || lowerName.includes('aerodrome')) {
            return {
                contractType: 'positionManager',
                assetTypes: ['ETH', 'TOKEN', 'LP'],
                tokenAddresses
            };
        }

        if (lowerName.includes('lptokenlocker') || lowerName.includes('locker')) {
            return {
                contractType: 'positionManager',
                assetTypes: ['ETH', 'TOKEN', 'LP', 'NFT'], // Can hold both LP tokens and NFTs
                tokenAddresses
            };
        }

        return {
            ...defaultConfig,
            tokenAddresses
        };
    }

    /**
     * Fetch ETH balance for an address
     */
    private async fetchETHBalance(address: string): Promise<AssetBalance | null> {
        try {
            const balance = await this.provider.getBalance(address);
            const formattedBalance = ethers.utils.formatEther(balance);

            return {
                type: 'ETH',
                symbol: 'ETH',
                balance: formattedBalance,
                decimals: 18
            };
        } catch (error) {
            logger.error(`Error fetching ETH balance for ${address}:`, error);
            return null;
        }
    }

    /**
     * Fetch WETH balance for an address
     */
    private async fetchWETHBalance(address: string, wethAddress?: string): Promise<AssetBalance | null> {
        if (!wethAddress) {
            logger.warn(`No WETH address provided for ${address}`);
            return null;
        }

        try {
            const wethContract = new ethers.Contract(
                wethAddress,
                [
                    'function balanceOf(address) view returns (uint256)',
                    'function symbol() view returns (string)',
                    'function decimals() view returns (uint8)'
                ],
                this.provider
            );

            const [balance, symbol, decimals] = await Promise.all([
                wethContract.balanceOf(address),
                wethContract.symbol().catch(() => 'WETH'),
                wethContract.decimals().catch(() => 18)
            ]);

            const formattedBalance = ethers.utils.formatUnits(balance, decimals);

            return {
                type: 'WETH',
                symbol,
                balance: formattedBalance,
                decimals
            };
        } catch (error) {
            logger.error(`Error fetching WETH balance for ${address}:`, error);
            return null;
        }
    }

    /**
     * Fetch token balance for an address
     */
    private async fetchTokenBalance(address: string, tokenAddress: string, expectedSymbol: string = 'TOKEN'): Promise<AssetBalance | null> {
        if (!tokenAddress) {
            return null;
        }

        try {
            const tokenContract = new ethers.Contract(
                tokenAddress,
                [
                    'function balanceOf(address) view returns (uint256)',
                    'function symbol() view returns (string)',
                    'function decimals() view returns (uint8)'
                ],
                this.provider
            );

            const [balance, symbol, decimals] = await Promise.all([
                tokenContract.balanceOf(address),
                tokenContract.symbol().catch(() => expectedSymbol),
                tokenContract.decimals().catch(() => 18)
            ]);

            const formattedBalance = ethers.utils.formatUnits(balance, decimals);

            return {
                type: 'TOKEN',
                symbol,
                balance: formattedBalance,
                decimals
            };
        } catch (error) {
            logger.error(`Error fetching ${expectedSymbol} balance for ${address}:`, error);
            return null;
        }
    }

    /**
     * Fetch NFT balance for an address (for position managers)
     */
    private async fetchNFTBalance(address: string, contractName: string): Promise<AssetBalance[]> {
        const nftBalances: AssetBalance[] = [];

        // Check if this is a Uniswap position manager
        if (contractName.toLowerCase().includes('uniswap')) {
            try {
                // Try to call getOwnedTokenIds if available
                const contract = new ethers.Contract(
                    address,
                    [
                        'function getOwnedTokenIds() view returns (uint256[])',
                        'function getPositionValue(uint256) view returns (tuple(uint256 tokenId, uint256 wethAmount, uint256 memerAmount, uint256 memerValueInETH, uint256 totalValueETH, uint256 lastUpdated))',
                        'function getTokenBalances() view returns (uint256, uint256)'
                    ],
                    this.provider
                );

                const tokenIds = await contract.getOwnedTokenIds();

                if (tokenIds.length > 0) {
                    // Get the total position value
                    try {
                        const positionValue = await contract.getPositionValue(tokenIds[0]);
                        const totalValueETH = ethers.utils.formatEther(positionValue.totalValueETH);
                        const wethAmount = ethers.utils.formatEther(positionValue.wethAmount);
                        const memerAmount = ethers.utils.formatEther(positionValue.memerAmount);

                        logger.log(`[MultiAssetFetcher] Uniswap position value: ${totalValueETH} ETH (${wethAmount} WETH, ${memerAmount} MEMER equivalent)`);

                        nftBalances.push({
                            type: 'NFT',
                            symbol: 'UNI-V3-POS',
                            balance: `${totalValueETH} ETH`,
                            tokenId: tokenIds.join(',')
                        });
                    } catch (valueError) {
                        logger.warn(`Could not get position value, using token count:`, valueError);
                        nftBalances.push({
                            type: 'NFT',
                            symbol: 'UNI-V3-POS',
                            balance: tokenIds.length.toString(),
                            tokenId: tokenIds.join(',')
                        });
                    }
                }
            } catch (error) {
                logger.debug(`No NFT balance found for ${address}:`, error);
            }
        }

        return nftBalances;
    }
    /**
     * Fetch token balance for Uniswap setup contracts using getTokenBalances()
     */
    private async fetchUniswapTokenBalances(address: string): Promise<AssetBalance[]> {
        const balances: AssetBalance[] = [];

        try {
            const contract = new ethers.Contract(
                address,
                [
                    'function getTokenBalances() view returns (uint256, uint256)',
                    'function i_token0() view returns (address)',
                    'function i_token1() view returns (address)'
                ],
                this.provider
            );

            const [balance0, balance1] = await contract.getTokenBalances();

            // Get token addresses to determine which is which
            try {
                const token0Address = await contract.i_token0();
                const token1Address = await contract.i_token1();

                // Determine which token is MEMER and which is WETH
                const memerAddress = this.deploymentDetails?.core?.memerCore?.addressForUI;

                if (token0Address.toLowerCase() === memerAddress?.toLowerCase()) {
                    // token0 is MEMER, token1 is WETH
                    balances.push({
                        type: 'TOKEN',
                        symbol: 'MEMER',
                        balance: ethers.utils.formatEther(balance0),
                        decimals: 18
                    });
                    balances.push({
                        type: 'WETH',
                        symbol: 'WETH',
                        balance: ethers.utils.formatEther(balance1),
                        decimals: 18
                    });
                } else {
                    // token1 is MEMER, token0 is WETH
                    balances.push({
                        type: 'WETH',
                        symbol: 'WETH',
                        balance: ethers.utils.formatEther(balance0),
                        decimals: 18
                    });
                    balances.push({
                        type: 'TOKEN',
                        symbol: 'MEMER',
                        balance: ethers.utils.formatEther(balance1),
                        decimals: 18
                    });
                }

                logger.log(`[MultiAssetFetcher] Uniswap token balances: MEMER=${ethers.utils.formatEther(token0Address.toLowerCase() === memerAddress?.toLowerCase() ? balance0 : balance1)}, WETH=${ethers.utils.formatEther(token0Address.toLowerCase() === memerAddress?.toLowerCase() ? balance1 : balance0)}`);
            } catch (tokenError) {
                logger.warn(`Could not determine token types, using generic labels:`, tokenError);
                balances.push({
                    type: 'TOKEN',
                    symbol: 'TOKEN0',
                    balance: ethers.utils.formatEther(balance0),
                    decimals: 18
                });
                balances.push({
                    type: 'TOKEN',
                    symbol: 'TOKEN1',
                    balance: ethers.utils.formatEther(balance1),
                    decimals: 18
                });
            }
        } catch (error) {
            logger.error(`Error fetching Uniswap token balances for ${address}:`, error);
        }

        return balances;
    }

    /**
     * Fetch LP token balance for an address (for Aerodrome setup)
     */
    private async fetchLPBalance(address: string, contractName: string): Promise<AssetBalance[]> {
        const lpBalances: AssetBalance[] = [];

        // Check if this is an Aerodrome setup contract
        if (contractName.toLowerCase().includes('aerodrome')) {
            try {
                // Get pools with liquidity first
                const contract = new ethers.Contract(
                    address,
                    [
                        'function getPoolsWithLiquidity() view returns (address[])',
                        'function getLPTokenBalance(address pool) view returns (uint256)'
                    ],
                    this.provider
                );

                const poolsWithLiquidity = await contract.getPoolsWithLiquidity();
                logger.log(`[MultiAssetFetcher] Aerodrome pools with liquidity: ${poolsWithLiquidity.length}`);

                // For each pool, check the ACTUAL ERC20 balance held by this contract
                for (const pool of poolsWithLiquidity) {
                    try {
                        // Query the actual ERC20 balance of the LP token held by this contract
                        const lpTokenContract = new ethers.Contract(
                            pool,
                            ['function balanceOf(address) view returns (uint256)', 'function symbol() view returns (string)'],
                            this.provider
                        );

                        const actualBalance = await lpTokenContract.balanceOf(address);
                        const formattedBalance = ethers.utils.formatEther(actualBalance);

                        if (parseFloat(formattedBalance) > 0) {
                            let symbol = 'AERO-LP';
                            try {
                                symbol = await lpTokenContract.symbol();
                            } catch (e) {
                                symbol = `POOL-${pool.substring(0, 8)}`;
                            }

                            lpBalances.push({
                                type: 'LP',
                                symbol: symbol,
                                balance: formattedBalance,
                                decimals: 18
                            });

                            logger.log(`[MultiAssetFetcher] Found actual LP balance: ${formattedBalance} ${symbol}`);
                        } else {
                            logger.log(`[MultiAssetFetcher] No actual LP balance for pool ${pool.substring(0, 8)}`);
                        }
                    } catch (poolError) {
                        logger.debug(`Error fetching actual LP balance for pool ${pool}:`, poolError);
                    }
                }
            } catch (error) {
                logger.debug(`Error fetching LP balances for ${address}:`, error);
            }
        }

        return lpBalances;
    }
    /**
     * Fetch locked assets from LPTokenLocker contract
     */
    private async fetchLockedAssets(address: string, contractName: string): Promise<AssetBalance[]> {
        const lockedAssets: AssetBalance[] = [];

        // Check if this is the LPTokenLocker contract
        if (contractName.toLowerCase().includes('lptokenlocker') || contractName.toLowerCase().includes('locker')) {
            try {
                const contract = new ethers.Contract(
                    address,
                    [
                        'function getLockedAssetsCount() view returns (uint256)',
                        'function getLockedAsset(uint256) view returns (uint8, address, uint256, uint256, bool, string)'
                    ],
                    this.provider
                );

                const lockedAssetsCount = await contract.getLockedAssetsCount();
                const count = lockedAssetsCount.toNumber();

                logger.log(`[MultiAssetFetcher] LPTokenLocker has ${count} locked assets`);

                for (let i = 0; i < count; i++) {
                    try {
                        const [tokenType, tokenAddress, tokenIdOrAmount, lockEndTime, isWithdrawn, dexSource] =
                            await contract.getLockedAsset(i);

                        if (!isWithdrawn) {
                            // TokenType: 0 = ERC20, 1 = ERC721
                            if (tokenType === 0) {
                                // ERC20 token (LP token)
                                const formattedAmount = ethers.utils.formatEther(tokenIdOrAmount);
                                lockedAssets.push({
                                    type: 'LP',
                                    symbol: `${dexSource}-LP`,
                                    balance: formattedAmount,
                                    decimals: 18
                                });
                                logger.log(`[MultiAssetFetcher] Locked LP token: ${formattedAmount} ${dexSource}-LP`);
                            } else if (tokenType === 1) {
                                // ERC721 token (NFT)
                                lockedAssets.push({
                                    type: 'NFT',
                                    symbol: `${dexSource}-NFT`,
                                    balance: '1',
                                    tokenId: tokenIdOrAmount.toString()
                                });
                                logger.log(`[MultiAssetFetcher] Locked NFT: ${dexSource}-NFT #${tokenIdOrAmount.toString()}`);
                            }
                        }
                    } catch (assetError) {
                        logger.error(`Error fetching locked asset ${i}:`, assetError);
                    }
                }
            } catch (error) {
                logger.error(`Error fetching locked assets for ${address}:`, error);
            }
        }

        return lockedAssets;
    }

    /**
     * Fetch all asset balances for a contract
     */
    async fetchAllAssetBalances(contractName: string, contractAddress: string): Promise<AssetBalance[]> {
        const config = this.getContractAssetConfig(contractName, contractAddress);
        const balances: AssetBalance[] = [];

        logger.log(`[MultiAssetFetcher] Fetching assets for ${contractName} (${config.contractType}):`, config.assetTypes);

        // Special handling for Uniswap setup contracts
        if (contractName.toLowerCase().includes('uniswapsetup')) {
            try {
                // Use the specialized Uniswap token balance fetcher
                const uniswapBalances = await this.fetchUniswapTokenBalances(contractAddress);
                balances.push(...uniswapBalances);

                // Also get NFT positions
                const nftBalances = await this.fetchNFTBalance(contractAddress, contractName);
                balances.push(...nftBalances);

                // Get ETH balance (should be 0 after liquidity provision)
                const ethBalance = await this.fetchETHBalance(contractAddress);
                if (ethBalance) balances.push(ethBalance);

                logger.log(`[MultiAssetFetcher] Found ${balances.length} assets for ${contractName}:`,
                    balances.map(b => `${b.symbol}: ${b.balance}`));

                return balances;
            } catch (error) {
                logger.error(`Error fetching Uniswap assets for ${contractName}:`, error);
                // Fall back to standard asset fetching
            }
        }

        // Special handling for LPTokenLocker - use locked assets instead of standard queries
        if (contractName.toLowerCase().includes('lptokenlocker') || contractName.toLowerCase().includes('locker')) {
            try {
                const lockedAssets = await this.fetchLockedAssets(contractAddress, contractName);
                balances.push(...lockedAssets);

                // Also get ETH balance
                const ethBalance = await this.fetchETHBalance(contractAddress);
                if (ethBalance) balances.push(ethBalance);

                logger.log(`[MultiAssetFetcher] Found ${balances.length} assets for ${contractName}:`,
                    balances.map(b => `${b.symbol}: ${b.balance}`));

                return balances;
            } catch (error) {
                logger.error(`Error fetching locked assets for ${contractName}:`, error);
                // Fall back to standard asset fetching
            }
        }

        // Standard asset fetching for other contracts
        for (const assetType of config.assetTypes) {
            try {
                switch (assetType) {
                    case 'ETH':
                        const ethBalance = await this.fetchETHBalance(contractAddress);
                        if (ethBalance) balances.push(ethBalance);
                        break;

                    case 'WETH':
                        const wethBalance = await this.fetchWETHBalance(contractAddress, config.tokenAddresses?.WETH);
                        if (wethBalance) balances.push(wethBalance);
                        break;

                    case 'TOKEN':
                        const tokenBalance = await this.fetchTokenBalance(contractAddress, config.tokenAddresses?.MEMER || '', 'MEMER');
                        if (tokenBalance) balances.push(tokenBalance);
                        break;

                    case 'NFT':
                        const nftBalances = await this.fetchNFTBalance(contractAddress, contractName);
                        balances.push(...nftBalances);
                        break;

                    case 'LP':
                        const lpBalances = await this.fetchLPBalance(contractAddress, contractName);
                        balances.push(...lpBalances);
                        break;
                }
            } catch (error) {
                logger.error(`Error fetching ${assetType} for ${contractName}:`, error);
            }
        }

        logger.log(`[MultiAssetFetcher] Found ${balances.length} assets for ${contractName}:`,
            balances.map(b => `${b.symbol}: ${b.balance}`));

        return balances;
    }

    /**
     * Batch fetch asset balances for multiple contracts
     */
    async batchFetchAssetBalances(contracts: Array<{ name: string; address: string }>): Promise<Record<string, AssetBalance[]>> {
        const results: Record<string, AssetBalance[]> = {};

        // Try to get WETH address dynamically if not in deployment details
        let dynamicWETHAddress: string | null = null;
        if (!this.deploymentDetails?.tokens?.WETH?.addressForUI) {
            dynamicWETHAddress = await this.getWETHAddress();
            if (dynamicWETHAddress) {
                logger.log(`[MultiAssetFetcher] Using dynamic WETH address: ${dynamicWETHAddress}`);
                // Update deployment details with the fetched WETH address
                if (!this.deploymentDetails.tokens) {
                    this.deploymentDetails.tokens = {};
                }
                this.deploymentDetails.tokens.WETH = { addressForUI: dynamicWETHAddress };
            }
        }

        // Process contracts in batches to avoid overwhelming the network
        const batchSize = 3;
        for (let i = 0; i < contracts.length; i += batchSize) {
            const batch = contracts.slice(i, i + batchSize);

            const batchPromises = batch.map(async (contract) => {
                const balances = await this.fetchAllAssetBalances(contract.name, contract.address);
                results[contract.address] = balances;
                return balances;
            });

            await Promise.all(batchPromises);

            // Small delay between batches
            if (i + batchSize < contracts.length) {
                await new Promise(resolve => setTimeout(resolve, 200));
            }
        }

        return results;
    }
}
